import { useEffect, useCallback, useState } from 'react';
import { useStressTestStore } from '../../store/stressTestStore';
import { useScenarioStore } from '../../store/scenarioStore';
import { useZoneStore } from '../../store/zoneStore';
import { StressTestScenarioParameters } from '../../types/scenario';
import { SaveScenarioModal } from './SaveScenarioModal';

interface PrerequisitesSectionProps {
  onZoneChange?: (zoneId: string) => void;
}

export function PrerequisitesSection({ onZoneChange }: PrerequisitesSectionProps) {
  const stressZoneId = useStressTestStore(s => s.stressZoneId);
  const setStressZoneId = useStressTestStore(s => s.setStressZoneId);
  const clearResults = useStressTestStore(s => s.clearResults);
  const activeScenarioId = useStressTestStore(s => s.activeScenarioId);
  const selectScenario = useStressTestStore(s => s.selectScenario);
  const applyScenarioParams = useStressTestStore(s => s.applyScenarioParams);
  const buildStressTestParams = useStressTestStore(s => s.buildStressTestParams);

  const {
    scenarioList,
    loadScenarioList,
    loadScenario: loadDbScenario,
    createScenario: createDbScenario,
    saveScenario: saveDbScenario,
    deleteScenario: deleteDbScenario,
    currentScenario,
    isSaving,
  } = useScenarioStore();

  const { zones, loadZones, isLoading: isZoneLoading } = useZoneStore();

  const [showSaveModal, setShowSaveModal] = useState(false);

  const stressScenarios = scenarioList.filter(s => s.scenarioType === 'stresstest');
  const isDbScenario = activeScenarioId != null && activeScenarioId !== '';

  useEffect(() => {
    loadZones().then(() => {
      // Default to the zone the user is already working in (map/monitoring
      // selection) instead of an empty "-- Zone --" — the stress page never
      // asks for something the app already knows (UI-Audit B4).
      const zoneToUse = stressZoneId ?? useZoneStore.getState().activeZoneId;
      if (zoneToUse) {
        handleZoneChange(zoneToUse);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadZones]);

  useEffect(() => {
    if (stressZoneId) {
      loadScenarioList(stressZoneId);
    }
  }, [stressZoneId, loadScenarioList]);

  const handleZoneChange = useCallback((zoneId: string) => {
    if (!zoneId) return;
    setStressZoneId(zoneId);
    clearResults();
    onZoneChange?.(zoneId);
  }, [clearResults, setStressZoneId, onZoneChange]);

  const handleScenarioSelect = useCallback(async (id: string) => {
    selectScenario(id);
    if (!id) {
      useScenarioStore.getState().clearCurrentScenario();
      return;
    }
    if (stressZoneId) {
      await loadDbScenario(id, stressZoneId);
      const scenario = useScenarioStore.getState().currentScenario;
      if (scenario?.parameters?.type === 'stresstest') {
        applyScenarioParams(scenario.parameters as StressTestScenarioParameters);
      }
    }
  }, [selectScenario, stressZoneId, loadDbScenario, applyScenarioParams]);

  const handleSaveAs = useCallback(async (name: string, description: string) => {
    if (!stressZoneId) return;
    const scenario = await createDbScenario({
      name,
      description: description || undefined,
      scenarioType: 'stresstest',
      parameters: buildStressTestParams(),
    }, stressZoneId);
    if (scenario) {
      selectScenario(scenario.id);
    }
    setShowSaveModal(false);
  }, [stressZoneId, createDbScenario, buildStressTestParams, selectScenario]);

  const handleUpdateScenario = useCallback(async () => {
    if (!stressZoneId || !activeScenarioId) return;
    await saveDbScenario(buildStressTestParams(), stressZoneId);
  }, [stressZoneId, activeScenarioId, saveDbScenario, buildStressTestParams]);

  const handleDeleteScenario = useCallback(async () => {
    if (!activeScenarioId || !stressZoneId) return;
    await deleteDbScenario(activeScenarioId, stressZoneId);
    selectScenario('');
  }, [activeScenarioId, stressZoneId, deleteDbScenario, selectScenario]);

  return (
    <div className="px-3 pb-2 space-y-2 border-b border-gray-200 dark:border-primary-600">
      {/* Zone */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-primary-400 font-semibold mb-1 block">Zone</label>
        <div className="relative">
          <select
            value={stressZoneId || ''}
            onChange={(e) => handleZoneChange(e.target.value)}
            disabled={isZoneLoading || zones.length === 0}
            className="w-full px-3 py-1.5 bg-white dark:bg-primary-700 border border-gray-300 dark:border-primary-500 rounded-lg text-sm font-medium text-gray-800 dark:text-primary-100 focus:outline-hidden focus:ring-2 focus:ring-accent-primary appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            {zones.length === 0 ? (
              <option value="">Keine Zonen</option>
            ) : (
              <>
                {!stressZoneId && <option value="" disabled>-- Zone --</option>}
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>{zone.name}</option>
                ))}
              </>
            )}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Szenario: saved overlay (Zubau, neue Hausanschlüsse) + base case */}
      {(
        <div>
          <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-primary-400 font-semibold mb-1 block">Szenario</label>
          <div className="flex gap-1.5">
            <div className="relative flex-1">
              <select
                value={activeScenarioId || ''}
                onChange={(e) => handleScenarioSelect(e.target.value)}
                className="w-full px-3 py-1.5 bg-white dark:bg-primary-700 border border-gray-300 dark:border-primary-500 rounded-lg text-sm font-medium text-gray-800 dark:text-primary-100 focus:outline-hidden focus:ring-2 focus:ring-accent-primary appearance-none cursor-pointer"
              >
                <option value="">Ohne Szenario (nur Grundzustand)</option>
                {stressScenarios.length > 0 && (
                  <optgroup label="Gespeicherte Szenarien">
                    {stressScenarios.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {/* Save button */}
            <button
              onClick={() => setShowSaveModal(true)}
              className="px-2 py-1.5 bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 dark:bg-primary-700 dark:hover:bg-primary-600 dark:text-primary-100 dark:border-primary-500 rounded-lg text-sm transition-all duration-200 shadow-sm focus:outline-hidden focus:ring-2 focus:ring-accent-primary"
              title="Änderungen und Grundzustand als Szenario speichern"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
            </button>
            {isDbScenario && (
              <button
                onClick={handleUpdateScenario}
                disabled={isSaving}
                className="px-2 py-1.5 bg-white hover:bg-blue-50 text-blue-600 border border-blue-300 dark:bg-primary-700 dark:hover:bg-blue-900/20 dark:text-blue-400 dark:border-blue-500 rounded-lg text-sm transition-all duration-200 shadow-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Szenario aktualisieren"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            {isDbScenario && (
              <button
                onClick={handleDeleteScenario}
                disabled={isSaving}
                className="px-2 py-1.5 bg-white hover:bg-red-50 text-red-600 border border-red-300 dark:bg-primary-700 dark:hover:bg-red-900/20 dark:text-red-400 dark:border-red-500 rounded-lg text-sm transition-all duration-200 shadow-sm focus:outline-hidden focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Szenario löschen"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
          {isDbScenario && (
            <p className="mt-1 text-xs text-gray-500 dark:text-primary-400">
              {currentScenario?.description || stressScenarios.find(s => s.id === activeScenarioId)?.description || 'Eigenes Szenario'}
            </p>
          )}
        </div>
      )}

      {showSaveModal && (
        <SaveScenarioModal
          onSave={handleSaveAs}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
}
